// Where the reader's tooltip bubble goes for a given anchor. Pure — no DOM, no
// React — so the awkward cases (a button in the column header with nothing
// above it, a control at the edge of the screen) are unit-tested rather than
// discovered by hovering.

/** The bubble's distance from the control it labels. */
export const TOOLTIP_GAP_PX = 6;
/** How close to the edge of the screen the bubble may come. */
export const TOOLTIP_MARGIN_PX = 8;

interface Box {
	left: number;
	top: number;
	width: number;
	height: number;
}

interface Size {
	width: number;
	height: number;
}

export interface TooltipPlacement {
	left: number;
	top: number;
	placement: "top" | "bottom";
}

export const tooltipPosition = (anchor: Box, bubble: Size, viewport: Size): TooltipPlacement => {
	// Above by default: the pointer is usually below the control it arrived at,
	// so a bubble underneath would sit where the hand is.
	const above = anchor.top - TOOLTIP_GAP_PX - bubble.height;
	const below = anchor.top + anchor.height + TOOLTIP_GAP_PX;
	// Flipped only when there is genuinely no room above AND there is room
	// below — at the bottom of the screen neither fits, and above is the side
	// that at least keeps the bubble on screen.
	const flip = above < TOOLTIP_MARGIN_PX && below + bubble.height <= viewport.height;

	const centred = anchor.left + anchor.width / 2 - bubble.width / 2;
	const rightmost = viewport.width - TOOLTIP_MARGIN_PX - bubble.width;
	// Math.max last, so a bubble wider than the screen starts at the margin
	// rather than being pushed off to the left of it.
	const left = Math.max(TOOLTIP_MARGIN_PX, Math.min(centred, rightmost));

	return { left, top: flip ? below : above, placement: flip ? "bottom" : "top" };
};
